import AsyncStorage from "@react-native-async-storage/async-storage";
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";

import { ApiOfflineError, setNetworkState } from "../lib/api";
import { isCentralHost } from "../lib/hosting";
import { useStaffAuth } from "./StaffAuthProvider";
import { AppContext, type AppContextValue } from "./app-store-context";

export { useAppStore } from "./app-store-context";

import { createSeedState, createPostImportSeedState, publicCopy } from "../data/seed";
import {
  monarchFuneralHome,
  monarchImportResult,
  monarchBilling,
  createMonarchNotifications,
} from "../data/monarch";
import { makeSlug } from "../lib/format";
import {
  AppDataState,
  BillingAccount,
  ConsentRecord,
  CreateMemorialPayload,
  CreateRSVPPayload,
  Event,
  EventAttendeeRow,
  FamilyToken,
  FuneralHome,
  ImportCandidate,
  ImportResult,
  MonitoringConfig,
  Notification,
  OnboardingStatus,
  WalkInPayload,
} from "../types/domain";
import {
  startMonitoring as startMon,
  stopMonitoring as stopMon,
  checkNow as checkMon,
} from "../lib/monitoring";
import { diffMemorial, applySyncResult } from "../lib/sync";
import {
  generateShortSlug,
  generateShortUrl,
  toFuneralHomeSlug,
  toDecedentSlug,
  shortLinkExpiresAt,
} from "../lib/short-links";
import { generatePermanentUrl } from "../lib/short-links";
import {
  getSystemNotificationErrorMessage,
  listCentralSystemNotifications,
  markAllCentralSystemNotificationsRead,
  markCentralSystemNotificationRead,
} from "../lib/central-system-notifications";

const STORAGE_KEY = "wehonor-store-v2";

function createId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function needsOperationalSeed(state: AppDataState) {
  return !state.funeralHome && state.memorials.length === 0 && state.events.length === 0;
}

function createCentralOperationalSeed(current: AppDataState): AppDataState {
  const seeded = createPostImportSeedState();
  return {
    ...seeded,
    publicLanguage: current.publicLanguage,
    notifications: [],
  };
}

function rankEventStatus(status: Event["status"]) {
  return ["live", "scheduled", "draft", "completed", "archived", "canceled"].indexOf(status);
}

function dedupeAttendee(
  state: AppDataState,
  memorialId: string,
  payload: { name: string; email?: string; phone?: string },
) {
  const normalizedName = payload.name.trim().toLowerCase();
  return state.attendees.find((attendee) => {
    if (attendee.memorialId !== memorialId) {
      return false;
    }
    if (payload.email && attendee.email?.toLowerCase() === payload.email.toLowerCase()) {
      return true;
    }
    if (payload.phone && attendee.phone === payload.phone) {
      return true;
    }
    return attendee.name.trim().toLowerCase() === normalizedName;
  });
}

export function AppProvider({ children }: { children: ReactNode }) {
  const staffAuth = useStaffAuth();
  const centralHost = isCentralHost();
  const [data, setData] = useState<AppDataState>(createSeedState);
  const [dataReady, setDataReady] = useState(false);
  const [notificationError, setNotificationError] = useState("");
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const staffSessionActive = staffAuth.status === "authenticated";
  const identitySupport =
    centralHost && staffAuth.identity?.access.role === "support";
  const ready = dataReady && (!centralHost || staffAuth.status !== "loading");

  const refreshNotifications = useCallback(async () => {
    if (
      !centralHost ||
      staffAuth.status !== "authenticated" ||
      identitySupport
    )
      return;
    setNotificationsLoading(true);
    setNotificationError("");
    try {
      const notifications = await listCentralSystemNotifications();
      setData((current) => ({
        ...current,
        notifications: notifications.map((notification) => ({
          actionUrl: notification.actionUrl ?? undefined,
          body: notification.body,
          caseId: notification.caseId ?? undefined,
          createdAt: notification.createdAt,
          deliveryStatus: notification.emailStatus,
          funeralHomeId: staffAuth.identity?.access.funeralHomeId ?? "",
          id: notification.id,
          read: notification.read,
          title: notification.title,
          type: notification.type,
        })),
      }));
    } catch (error) {
      setNotificationError(getSystemNotificationErrorMessage(error));
    } finally {
      setNotificationsLoading(false);
    }
  }, [
    centralHost,
    identitySupport,
    staffAuth.identity?.access.funeralHomeId,
    staffAuth.status,
  ]);

  useEffect(() => {
    // Sync network state to the API client.
    // On web, navigator.onLine is available; on native, assume online until
    // a real NetInfo integration is added.
    if (Platform.OS === "web" && typeof navigator !== "undefined") {
      setNetworkState(navigator.onLine);
      const handleOnline = () => setNetworkState(true);
      const handleOffline = () => setNetworkState(false);
      window.addEventListener("online", handleOnline);
      window.addEventListener("offline", handleOffline);
      return () => {
        window.removeEventListener("online", handleOnline);
        window.removeEventListener("offline", handleOffline);
      };
    } else {
      setNetworkState(true);
    }
  }, []);

  useEffect(() => {
    let active = true;
    AsyncStorage.getItem(STORAGE_KEY)
      .then((raw) => {
        if (!active) return;
        if (raw) {
          setData(JSON.parse(raw) as AppDataState);
        }
      })
      .finally(() => {
        if (active) setDataReady(true);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!ready) return;
    AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(data)).catch(() => undefined);
  }, [data, ready]);

  useEffect(() => {
    if (!ready || !staffSessionActive || !centralHost || identitySupport)
      return;
    setData((current) =>
      needsOperationalSeed(current) ? createCentralOperationalSeed(current) : current,
    );
  }, [centralHost, identitySupport, ready, staffSessionActive]);

  useEffect(() => {
    if (!ready || !staffSessionActive || !centralHost || identitySupport)
      return;
    void refreshNotifications();
    const interval = setInterval(() => void refreshNotifications(), 60_000);
    return () => clearInterval(interval);
  }, [
    centralHost,
    identitySupport,
    ready,
    refreshNotifications,
    staffSessionActive,
  ]);

  const handleNewCaseRef = useRef<((candidate: ImportCandidate) => void) | null>(null);
  const handleExistingUpdateRef = useRef<((targetMemorialId: string, candidate: ImportCandidate, changeDescription: string) => void) | null>(null);

  handleNewCaseRef.current = (candidate: ImportCandidate) => {
    const memorialId = `mem-${candidate.decedentName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-${Math.random().toString(36).slice(2, 6)}`;
    const now = new Date().toISOString();
    setData((current) => {
      const existingSlugs = current.memorials.map((m) => m.shortSlug).filter(Boolean) as string[];
      const fhName = current.funeralHome?.name ?? "WeHonor";
      const shortSlug = generateShortSlug(candidate.decedentName, existingSlugs);
      const newMemorial = {
        id: memorialId,
        decedentName: candidate.decedentName,
        dob: candidate.dob,
        dod: candidate.dod,
        obituaryUrl: candidate.obituaryUrl,
        photoUrl: candidate.photoUrl,
        funeralHomeId: current.funeralHome?.id ?? "fh-monarch",
        status: "published" as const,
        familyContact: "",
        themeNote: "",
        heroQuote: "A life worth gathering around.",
        productLinks: candidate.productLinks,
        shortSlug,
        shortUrl: generateShortUrl(shortSlug),
        permanentUrl: generatePermanentUrl(toFuneralHomeSlug(fhName), toDecedentSlug(candidate.decedentName)),
        shortLinkExpiresAt: shortLinkExpiresAt(now),
        syncStatus: "synced" as const,
        lastSyncAt: now,
        syncCount: 1,
      };
      return {
        ...current,
        memorials: [newMemorial, ...current.memorials],
        events: [
          ...candidate.services.map((svc, i) => ({
            id: `evt-${memorialId}-${svc.type}-${i}`,
            memorialId,
            type: svc.type,
            title: svc.title,
            startAt: `${svc.date}T${svc.time}:00.000Z`,
            endAt: svc.endTime ? `${svc.date}T${svc.endTime}:00.000Z` : `${svc.date}T${svc.time}:00.000Z`,
            venue: svc.venue,
            address: svc.address,
            livestreamUrl: svc.livestreamUrl,
            visibility: "public" as const,
            capacity: 200,
            status: "scheduled" as const,
            rsvpEnabled: true,
            checkInEnabled: true,
            qrMode: "combined" as const,
            confirmationHeadline: "We will hold space for you.",
            confirmationBody: "Your RSVP has been recorded.",
          })),
          ...current.events,
        ],
        notifications: [
          {
            id: `notif-newcase-${Date.now()}`,
            funeralHomeId: current.funeralHome?.id ?? "fh-monarch",
            type: "service_ready" as const,
            title: `Event Kit for ${candidate.decedentName} is Ready`,
            body: `We auto-detected a new obituary for ${candidate.decedentName} and created the event page, RSVP link, and check-in tools.`,
            read: false,
            memorialId,
            createdAt: now,
          },
          ...current.notifications,
        ],
        funeralHome: current.funeralHome
          ? {
              ...current.funeralHome,
              monitoring: current.funeralHome.monitoring
                ? {
                    ...current.funeralHome.monitoring,
                    lastScanAt: now,
                    newCasesDetected: (current.funeralHome.monitoring.newCasesDetected ?? 0) + 1,
                  }
                : undefined,
            }
          : undefined,
      };
    });
  };

  handleExistingUpdateRef.current = (targetMemorialId: string, candidate: ImportCandidate, changeDescription: string) => {
    const now = new Date().toISOString();
    setData((current) => {
      const memorial = current.memorials.find((m) => m.id === targetMemorialId);
      if (!memorial) return current;
      const existingEvents = current.events.filter((e) => e.memorialId === targetMemorialId);
      const result = diffMemorial(memorial, existingEvents, candidate);
      if (!result.hasChanges) return current;
      const applied = applySyncResult(
        targetMemorialId,
        result,
        current.memorials,
        current.events,
        (svc, i) => `evt-${targetMemorialId}-sync-${svc.type}-${i}-${Date.now()}`,
        now,
      );
      const notif = {
        id: `notif-sync-${Date.now()}`,
        funeralHomeId: current.funeralHome?.id ?? "fh-monarch",
        type: "service_updated" as const,
        title: `Update Detected — ${memorial.decedentName}`,
        body: changeDescription,
        read: false,
        memorialId: targetMemorialId,
        createdAt: now,
        changes: result.changeLog,
      };
      return {
        ...current,
        memorials: applied.memorials,
        events: applied.events,
        notifications: [notif, ...current.notifications],
        funeralHome: current.funeralHome
          ? {
              ...current.funeralHome,
              monitoring: current.funeralHome.monitoring
                ? { ...current.funeralHome.monitoring, lastScanAt: now }
                : undefined,
            }
          : undefined,
      };
    });
  };

  const monitoringConfig = data.funeralHome?.monitoring;

  useEffect(() => {
    if (ready && monitoringConfig?.enabled) {
      const stableOnNewCase = (candidate: ImportCandidate) => {
        handleNewCaseRef.current?.(candidate);
      };
      const stableOnExistingUpdate = (targetMemorialId: string, candidate: ImportCandidate, changeDescription: string) => {
        handleExistingUpdateRef.current?.(targetMemorialId, candidate, changeDescription);
      };
      startMon(monitoringConfig, stableOnNewCase, stableOnExistingUpdate);
    } else if (ready) {
      stopMon();
    }
    return () => {
      stopMon();
    };
  }, [ready, monitoringConfig?.enabled, monitoringConfig?.intervalMinutes]);

  const value = useMemo<AppContextValue>(() => {

    const sortedEvents = [...data.events].sort(
      (left, right) =>
        rankEventStatus(left.status) - rankEventStatus(right.status) ||
        new Date(left.startAt).getTime() - new Date(right.startAt).getTime(),
    );

    const getEvent = (eventId: string) => data.events.find((event) => event.id === eventId);

    return {
      data: { ...data, events: sortedEvents },
      ready,
      staffSessionActive,
      publicCopy,
      unreadNotificationCount: data.notifications.filter((n) => !n.read).length,
      notificationError,
      notificationsLoading,
      refreshNotifications,
      setLanguage: (code) => setData((current) => ({ ...current, publicLanguage: code })),
      logout: async () => {
        if (centralHost) await staffAuth.signOut();
        await AsyncStorage.removeItem(STORAGE_KEY).catch(() => undefined);
        setData(createSeedState());
      },
      getMemorial: (memorialId) =>
        data.memorials.find((memorial) => memorial.id === memorialId),
      getEvent,
      getMemorialEvents: (memorialId) =>
        sortedEvents.filter((event) => event.memorialId === memorialId),
      getEventRows: (eventId) => {
        const event = getEvent(eventId);
        if (!event) return [];
        const latestCheckIns = new Map(
          data.checkIns
            .filter((checkIn) => checkIn.eventId === eventId)
            .map((checkIn) => [checkIn.attendeeId, checkIn]),
        );
        return data.attendees
          .filter((attendee) => attendee.memorialId === event.memorialId)
          .map((attendee) => ({
            attendee,
            rsvp: data.rsvps.find(
              (rsvp) => rsvp.eventId === eventId && rsvp.attendeeId === attendee.id,
            ),
            checkIn: latestCheckIns.get(attendee.id),
            consent: data.consents.find(
              (consent) => consent.eventId === eventId && consent.attendeeId === attendee.id,
            ),
          }))
          .filter((row) => row.rsvp || row.checkIn || row.attendee.source === "walk_in_manual")
          .sort((left, right) => left.attendee.name.localeCompare(right.attendee.name));
      },
      getRecentArrivals: (eventId) =>
        data.checkIns
          .filter((checkIn) => checkIn.eventId === eventId)
          .sort(
            (left, right) =>
              new Date(right.checkedInAt).getTime() - new Date(left.checkedInAt).getTime(),
          )
          .slice(0, 6)
          .map((checkIn) => ({
            attendee: data.attendees.find((attendee) => attendee.id === checkIn.attendeeId)!,
            rsvp: data.rsvps.find(
              (rsvp) => rsvp.eventId === eventId && rsvp.attendeeId === checkIn.attendeeId,
            ),
            checkIn,
            consent: data.consents.find(
              (consent) =>
                consent.eventId === eventId && consent.attendeeId === checkIn.attendeeId,
            ),
          })),
      createRSVP: (eventId, payload) => {
        const event = getEvent(eventId);
        if (!event) throw new Error("Event not found");
        const existing = dedupeAttendee(data, event.memorialId, payload);
        const attendeeId = existing?.id ?? createId("att");
        const submittedAt = new Date().toISOString();
        setData((current) => {
          const attendee = existing ?? {
            id: attendeeId,
            memorialId: event.memorialId,
            name: payload.name,
            email: payload.email,
            phone: payload.phone,
            address: payload.address,
            relationship: payload.relationship,
            source: "public_rsvp" as const,
            createdAt: submittedAt,
          };
          const attendees = existing
            ? current.attendees.map((entry) =>
                entry.id === existing.id
                  ? {
                      ...entry,
                      email: payload.email || entry.email,
                      phone: payload.phone || entry.phone,
                      address: payload.address || entry.address,
                      relationship: payload.relationship || entry.relationship,
                    }
                  : entry,
              )
            : [...current.attendees, attendee];
          const existingRsvp = current.rsvps.find(
            (entry) => entry.eventId === eventId && entry.attendeeId === attendeeId,
          );
          const rsvps = existingRsvp
            ? current.rsvps.map((entry) =>
                entry.id === existingRsvp.id
                  ? { ...entry, responseStatus: payload.responseStatus, partySize: payload.partySize, note: payload.note, submittedAt }
                  : entry,
              )
            : [
                ...current.rsvps,
                {
                  id: createId("rsvp"),
                  attendeeId,
                  eventId,
                  responseStatus: payload.responseStatus,
                  partySize: payload.partySize,
                  note: payload.note,
                  submittedAt,
                },
              ];
          const nextConsents = [...current.consents];
          const consentIndex = nextConsents.findIndex(
            (entry) => entry.attendeeId === attendeeId && entry.eventId === eventId,
          );
          const currentConsent = consentIndex >= 0 ? nextConsents[consentIndex] : undefined;
          const consentRecord: ConsentRecord = {
            id: currentConsent?.id ?? createId("consent"),
            attendeeId,
            eventId,
            consentType: "preneed",
            version: "2026-03",
            granted: payload.consentGranted,
            grantedAt: submittedAt,
            source: "rsvp",
          };
          if (consentIndex >= 0) {
            nextConsents[consentIndex] = consentRecord;
          } else {
            nextConsents.push(consentRecord);
          }
          return { ...current, attendees, rsvps, consents: nextConsents };
        });
        return { attendeeId };
      },
      toggleCheckIn: (eventId, attendeeId) => {
        const duplicate = data.checkIns.some(
          (checkIn) => checkIn.eventId === eventId && checkIn.attendeeId === attendeeId,
        );
        if (duplicate) return { duplicate: true };
        const checkInId = createId("checkin");
        setData((current) => ({
          ...current,
          checkIns: [
            ...current.checkIns,
            {
              id: checkInId,
              attendeeId,
              eventId,
              checkedInAt: new Date().toISOString(),
              checkedInBy: "Front desk",
              method: "search",
              deviceId: "expo-demo",
            },
          ],
        }));
        return { duplicate: false, checkInId };
      },
      addWalkIn: (eventId, payload) => {
        const event = getEvent(eventId);
        if (!event) throw new Error("Event not found");
        const existing = dedupeAttendee(data, event.memorialId, payload);
        const attendeeId = existing?.id ?? createId("att");
        const createdAt = new Date().toISOString();
        setData((current) => {
          const attendees = existing
            ? current.attendees
            : [
                ...current.attendees,
                {
                  id: attendeeId,
                  memorialId: event.memorialId,
                  name: payload.name,
                  email: payload.email,
                  phone: payload.phone,
                  relationship: payload.relationship,
                  source: "walk_in_manual" as const,
                  createdAt,
                },
              ];
          const consents = payload.consentGranted
            ? [
                ...current.consents,
                {
                  id: createId("consent"),
                  attendeeId,
                  eventId,
                  consentType: "follow_up" as const,
                  version: "2026-03",
                  granted: true,
                  grantedAt: createdAt,
                  source: "walk_in" as const,
                },
              ]
            : current.consents;
          return {
            ...current,
            attendees,
            consents,
            checkIns: [
              ...current.checkIns,
              {
                id: createId("checkin"),
                attendeeId,
                eventId,
                checkedInAt: createdAt,
                checkedInBy: "Front desk",
                method: "walk_in",
                deviceId: "expo-demo",
              },
            ],
          };
        });
        return { attendeeId };
      },
      createMemorialWithEvents: (payload) => {
        const memorialId = `mem-${makeSlug(payload.decedentName)}-${Math.random()
          .toString(36)
          .slice(2, 6)}`;
        setData((current) => ({
          ...current,
          memorials: [
            {
              id: memorialId,
              decedentName: payload.decedentName,
              funeralHomeId: current.funeralHome?.id ?? "fh-monarch",
              status: "draft",
              familyContact: payload.familyContact,
              obituaryUrl: payload.obituaryUrl,
              photoUrl: payload.photoUrl,
              themeNote: payload.themeNote,
              heroQuote: "A life worth gathering around.",
              productLinks: payload.productLinks,
            },
            ...current.memorials,
          ],
          events: [
            ...payload.events.map((entry) => ({
              id: `evt-${makeSlug(payload.decedentName)}-${makeSlug(entry.title)}`,
              memorialId,
              type: entry.type,
              title: entry.title,
              startAt: entry.startAt,
              endAt: entry.endAt,
              venue: entry.venue,
              address: entry.address,
              livestreamUrl: entry.livestreamUrl,
              visibility: "public" as const,
              capacity: 200,
              status: "draft" as const,
              rsvpEnabled: true,
              checkInEnabled: true,
              qrMode: "combined" as const,
              confirmationHeadline: "We will hold space for you.",
              confirmationBody: "Your RSVP has been recorded.",
            })),
            ...current.events,
          ],
        }));
        return { memorialId };
      },
      completeOnboarding: (funeralHome: FuneralHome) => {
        setData((current) => ({
          ...current,
          funeralHome,
          onboardingComplete: true,
          billing: monarchBilling,
        }));
      },
      runImport: async () => {
        const postImport = createPostImportSeedState();
        const notifications = createMonarchNotifications();
        setData((current) => ({
          ...current,
          memorials: postImport.memorials,
          events: postImport.events,
          attendees: postImport.attendees,
          rsvps: postImport.rsvps,
          checkIns: postImport.checkIns,
          consents: postImport.consents,
          familyTokens: postImport.familyTokens,
          importResult: monarchImportResult,
          notifications: [...notifications, ...current.notifications],
        }));
        return monarchImportResult;
      },
      confirmImport: () => {
        setData((current) => ({
          ...current,
          importResult: monarchImportResult,
        }));
      },
      markNotificationRead: (notificationId: string) => {
        setData((current) => ({
          ...current,
          notifications: current.notifications.map((n) =>
            n.id === notificationId ? { ...n, read: true } : n,
          ),
        }));
        if (centralHost) void markCentralSystemNotificationRead(notificationId).catch(() => void refreshNotifications());
      },
      markAllNotificationsRead: () => {
        setData((current) => ({
          ...current,
          notifications: current.notifications.map((n) => ({ ...n, read: true })),
        }));
        if (centralHost) void markAllCentralSystemNotificationsRead().catch(() => void refreshNotifications());
      },
      getMemorialByShortSlug: (shortSlug: string) =>
        data.memorials.find((m) => m.shortSlug === shortSlug),
      generateFamilyLink: (memorialId: string, familyName: string) => {
        const token = `${memorialId.replace("mem-", "")}-fam-${Math.random().toString(36).slice(2, 10)}`;
        const now = new Date().toISOString();
        const expiresAt = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString();
        const familyToken: FamilyToken = {
          id: createId("ft"),
          token,
          memorialId,
          familyName,
          createdAt: now,
          expiresAt,
          createdBy: "staff",
        };
        setData((current) => ({
          ...current,
          familyTokens: [familyToken, ...(current.familyTokens ?? [])],
        }));
        return { token, url: `https://wehonor.com/family/${token}` };
      },
      getFamilySession: (token: string) => {
        const familyToken = (data.familyTokens ?? []).find((ft) => ft.token === token);
        if (!familyToken) return null;
        if (new Date() > new Date(familyToken.expiresAt)) return null;
        const memorial = data.memorials.find((m) => m.id === familyToken.memorialId);
        if (!memorial) return null;
        const events = data.events.filter((e) => e.memorialId === memorial.id);
        const rsvpCount = data.rsvps.filter((r) =>
          events.some((e) => e.id === r.eventId),
        ).length;
        return { memorial, events, rsvpCount, familyToken };
      },
      startMonitoring: () => {
        setData((current) => ({
          ...current,
          funeralHome: current.funeralHome
            ? {
                ...current.funeralHome,
                monitoring: current.funeralHome.monitoring
                  ? { ...current.funeralHome.monitoring, enabled: true }
                  : { enabled: true, intervalMinutes: 30, newCasesDetected: 0 },
              }
            : undefined,
        }));
      },
      stopMonitoring: () => {
        setData((current) => ({
          ...current,
          funeralHome: current.funeralHome
            ? {
                ...current.funeralHome,
                monitoring: current.funeralHome.monitoring
                  ? { ...current.funeralHome.monitoring, enabled: false }
                  : undefined,
              }
            : undefined,
        }));
      },
      checkNow: () => {
        let found = false;
        const handleNewCase = (candidate: ImportCandidate) => {
          found = true;
          handleNewCaseRef.current?.(candidate);
        };
        const handleExistingUpdate = (targetMemorialId: string, candidate: ImportCandidate, changeDescription: string) => {
          found = true;
          handleExistingUpdateRef.current?.(targetMemorialId, candidate, changeDescription);
        };
        checkMon(handleNewCase, handleExistingUpdate);
        return found;
      },
      updateMonitoringConfig: (updates: Partial<MonitoringConfig>) => {
        setData((current) => ({
          ...current,
          funeralHome: current.funeralHome
            ? {
                ...current.funeralHome,
                monitoring: {
                  enabled: true,
                  intervalMinutes: 30,
                  newCasesDetected: 0,
                  ...current.funeralHome.monitoring,
                  ...updates,
                },
              }
            : undefined,
        }));
      },
      updateMemorialNotes: (memorialId: string, updates: { editableNotes?: string; heroQuote?: string }) => {
        setData((current) => ({
          ...current,
          memorials: current.memorials.map((m) =>
            m.id === memorialId ? { ...m, ...updates } : m,
          ),
        }));
      },
      updateEventNotes: (eventId: string, updates: { specialInstructions?: string; parkingNotes?: string; dressNotes?: string }) => {
        setData((current) => ({
          ...current,
          events: current.events.map((e) =>
            e.id === eventId ? { ...e, ...updates } : e,
          ),
        }));
      },
      syncExistingMemorial: (memorialId, candidate, changeDescription) => {
        const memorial = data.memorials.find((m) => m.id === memorialId);
        if (!memorial) return { applied: false, changeCount: 0 };
        const existingEvents = data.events.filter((e) => e.memorialId === memorialId);
        const result = diffMemorial(memorial, existingEvents, candidate);
        if (!result.hasChanges) return { applied: false, changeCount: 0 };

        const now = new Date().toISOString();
        setData((current) => {
          const applied = applySyncResult(
            memorialId,
            result,
            current.memorials,
            current.events,
            (svc, i) => `evt-${memorialId}-sync-${svc.type}-${i}-${Date.now()}`,
            now,
          );
          const summaryLines = result.changeLog.map((c) => `• ${c.description}`).join("\n");
          const notif = {
            id: `notif-sync-${Date.now()}`,
            funeralHomeId: current.funeralHome?.id ?? "fh-monarch",
            type: "service_updated" as const,
            title: `Update Detected — ${memorial.decedentName}`,
            body: changeDescription ?? `${result.changeLog.length} change${result.changeLog.length !== 1 ? "s" : ""} found on the funeral home website.\n\n${summaryLines}`,
            read: false,
            memorialId,
            createdAt: now,
            changes: result.changeLog,
          };
          return {
            ...current,
            memorials: applied.memorials,
            events: applied.events,
            notifications: [notif, ...current.notifications],
            funeralHome: current.funeralHome
              ? {
                  ...current.funeralHome,
                  monitoring: current.funeralHome.monitoring
                    ? { ...current.funeralHome.monitoring, lastScanAt: now }
                    : undefined,
                }
              : undefined,
          };
        });

        return { applied: true, changeCount: result.changeLog.length };
      },
      getLastSyncTime: (memorialId) => {
        const memorial = data.memorials.find((m) => m.id === memorialId);
        return memorial?.lastSyncAt ?? null;
      },
    };
  }, [centralHost, data, ready, staffAuth, staffSessionActive]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
